import {
  BackendQueryKeys,
  useTypeSafeMutation,
  useTypeSafeQuery,
} from '@oresundsbron/api';
import {
  Banner,
  Box,
  Button,
  Spinner,
  Typography,
} from '@oresundsbron/bridge-ui';
import { Trans, useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { toCurrency } from '../../lib/currency';
import { ConfirmRefundModal } from './ConfirmRefundModal';

export const Refund = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });

  const [isModalOpen, setModalOpen] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const refund = router.query.refund as string;

  useEffect(() => {
    if (!refund) {
      router.push(pathname);
    }
  }, [pathname, refund, router]);

  const {
    data,
    isLoading,
    isError: notFound,
  } = useTypeSafeQuery(
    BackendQueryKeys.ITicketRefund({ transactionId: refund }),
    (req) => req.ITicket.refund(refund),
    { enabled: !!refund, retry: 2 }
  );

  const {
    mutate: refundOrder,
    isError,
    isSuccess,
  } = useTypeSafeMutation(['iticket', 'refund'], (req) => req.ITicket.refund, {
    onError: (err) => {
      setModalOpen(false);
      return err;
    },
  });

  if (notFound) {
    return (
      <Box className="col-span-full md:col-span-6">
        <Typography as="h2" intent="title" gutter>
          {t('refund.notFound.title')}
        </Typography>
        <Typography>{t('refund.notFound.description')}</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className="item-center col-span-full flex h-[600px] items-center justify-center text-5xl text-primary-600 md:col-span-6">
        <Spinner label={t('action.loading')} />
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box className="col-span-full md:col-span-6">
        <Typography as="h2" intent="title" gutter>
          {t('refund.success.title')}
        </Typography>
        <Typography>{t('refund.success.description')}</Typography>
      </Box>
    );
  }

  return (
    <Box className="col-span-full md:col-span-6">
      <Typography as="h2" intent="title" gutter>
        {t('refund.title')}
      </Typography>
      {!data?.refundAllowed ? (
        <Banner color="warn" className="mb-4">
          <Typography as="span">{t('refund.notAllowed')}</Typography>
        </Banner>
      ) : null}
      <Trans i18nKey="refund.description" ns="onlineTicket">
        <Typography gutter />
        <Typography gutter />
      </Trans>
      <Typography intent="title" className="my-4">
        {t('refund.yourPurchase')}
      </Typography>
      <Banner>
        {data?.orderNo ? (
          <Typography className="mb-2">
            {t('refund.labels.orderId')}
            <Typography as="span" weight="medium" className="ml-1">
              {data.orderNo}
            </Typography>
          </Typography>
        ) : null}
        {data?.vechicleNo ? (
          <Typography className="mb-2">
            {t('refund.labels.myVehicle')}
            <Typography as="span" weight="medium" className="ml-1">
              {data.vechicleNo}
            </Typography>
          </Typography>
        ) : null}

        {data?.ticketCount ? (
          <Typography className="mb-2">
            {t('refund.labels.ticketType')}
            <Typography as="span" weight="medium" className="ml-1">
              {data.ticketCount > 1
                ? t('refund.labels.roundTrip')
                : t('refund.labels.oneWay')}
            </Typography>
          </Typography>
        ) : null}

        {data?.price ? (
          <Typography className="mb-2">
            {t('refund.labels.price')}
            <Typography as="span" weight="medium" className="ml-1">
              {toCurrency(data.price, router.locale)}
            </Typography>
          </Typography>
        ) : null}
      </Banner>

      {isError && (
        <Banner color="warn" className="mt-4">
          <Typography as="span">{t('refund.error.description')}</Typography>
        </Banner>
      )}

      <Box className="mt-6 flex justify-end">
        <Button
          as="button"
          disabled={!data?.refundAllowed || isError}
          onClick={() => setModalOpen(true)}
        >
          {t('refund.labels.refund')}
        </Button>

        {isModalOpen && (
          <ConfirmRefundModal
            onClose={() => setModalOpen(false)}
            onConfirm={() => refundOrder({ transactionId: refund })}
          />
        )}
      </Box>
    </Box>
  );
};
